import { useEffect, useRef } from 'react'
import { Button, NonIdealState, Spinner, SpinnerSize } from '@blueprintjs/core'

import { Scene } from '../viewport/Scene'
import type { Store } from '../store'

/**
 * The 3D canvas pane. Phase 1 renders a grid + triad empty state and
 * attaches the Scene when a part is loaded. Phase 4 adds the view
 * cube, HUD toolbar and face raycast picking.
 */
export function ViewportPanel({ store }: { store: Store }) {
  const canvasHost = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<Scene | null>(null)

  useEffect(() => {
    if (!canvasHost.current) return
    const s = new Scene(canvasHost.current)
    sceneRef.current = s
    s.start()
    return () => s.dispose()
  }, [])

  useEffect(() => {
    sceneRef.current?.setTheme(store.preferences.theme)
  }, [store.preferences.theme])

  // Picking is only active when the user clicked a "faces" picker button
  // in the PropertyManager (store.pickMode holds the operation id).
  useEffect(() => {
    sceneRef.current?.setPickCallback(store.pickMode ? (idx) => store.pickFace(idx) : null)
  }, [store.pickMode, store])

  // Sync part + toolpaths + operations into the scene.
  useEffect(() => {
    sceneRef.current?.syncJob(store.job)
  }, [store.job])

  useEffect(() => {
    sceneRef.current?.setFrame(store.scrubFrame)
  }, [store.scrubFrame])

  return (
    <div className="viewport-root">
      <div ref={canvasHost} className="viewport-canvas" />
      {!store.job.part && !store.uploadingStep && (
        <div className="viewport-overlay">
          <NonIdealState
            icon="document-open"
            title="Open a STEP file"
            description="Drag a .step/.stp file here, or use the Part ribbon."
            action={<Button icon="folder-open" text="Open STEP…" onClick={() => store.openStepDialog()} />}
          />
        </div>
      )}
      {store.uploadingStep && (
        <div className="viewport-overlay">
          <div className="viewport-busy">
            <Spinner size={SpinnerSize.LARGE} />
            <div className="viewport-busy-title">Processing STEP…</div>
            <div className="viewport-busy-sub">
              OCCT face classification + tessellation. Typically 5–20 s for a worm part.
            </div>
          </div>
        </div>
      )}
      <div className="viewport-hud">
        <Button icon="eye-open" minimal title="Iso" onClick={() => sceneRef.current?.view('iso')} />
        <Button icon="chevron-up" minimal title="Top" onClick={() => sceneRef.current?.view('top')} />
        <Button icon="chevron-right" minimal title="Right" onClick={() => sceneRef.current?.view('right')} />
        <Button icon="chevron-down" minimal title="Front" onClick={() => sceneRef.current?.view('front')} />
        <Button icon="zoom-to-fit" minimal title="Fit (F)" onClick={() => sceneRef.current?.zoomToFit()} />
      </div>
    </div>
  )
}
